import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChartBar, Trophy, Lightning, CheckCircle, Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BacktestPanel — fetches /api/analysis/backtest/{endpoint}
 * Renders F1 vs F2 hit-rate readout (top-1/3/5) + auto-tune verdict if present.
 *
 * Props:
 *   endpoint: 'sum-range' | 'sign-pattern'
 */
export default function BacktestPanel({ endpoint }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/analysis/backtest/${endpoint}`, { withCredentials: true })
      .then(res => { if (mounted) setData(res.data); })
      .catch(err => { if (mounted) setError(err.message || 'Failed'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [endpoint]);

  if (loading) return (
    <div className="bg-white border-2 border-violet-200 rounded-xl p-5 text-sm text-gray-500" data-testid="backtest-loading">
      Loading backtest results... (first run may take 4–8 seconds, subsequent loads are instant)
    </div>
  );
  if (error) return <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">{error}</div>;
  if (!data?.backtest?.n) return null;

  const bt = data.backtest;
  const tune = data.tune;
  const f1Wins = (bt.f1?.top3_pct || 0) >= (bt.f2?.top3_pct || 0);

  return (
    <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 border-2 border-violet-300 rounded-xl p-5 shadow-lg" data-testid="backtest-panel">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <ChartBar size={24} weight="fill" className="text-violet-700" />
        <div>
          <h3 className="text-lg font-black text-violet-900">📊 Backtest Accuracy — Last {bt.n} draws</h3>
          <p className="text-[11px] text-violet-700">Re-computed predictions on each historical draw using ONLY data available before that draw. Cached until next new draw.</p>
        </div>
      </div>

      {/* Hit-rate cards */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        {/* F1 card */}
        <div className={`bg-white border-2 ${f1Wins ? 'border-emerald-400 ring-2 ring-emerald-200' : 'border-blue-300'} rounded-xl p-4 shadow-sm`} data-testid="bt-f1-card">
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-blue-600 text-white text-xs font-black px-2 py-0.5 rounded">F1</span>
            <span className="text-sm font-bold text-blue-900">Long-Term · Multiplicative</span>
            {f1Wins && <CheckCircle size={18} weight="fill" className="text-emerald-600 ml-auto" />}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <HitCell label="Top-1" pct={bt.f1?.top1_pct} hits={bt.f1?.top1_hits} n={bt.n} accent="blue" />
            <HitCell label="Top-3" pct={bt.f1?.top3_pct} hits={bt.f1?.top3_hits} n={bt.n} accent="blue" highlight />
            <HitCell label="Top-5" pct={bt.f1?.top5_pct} hits={bt.f1?.top5_hits} n={bt.n} accent="blue" />
          </div>
        </div>

        {/* F2 card */}
        <div className={`bg-white border-2 ${!f1Wins ? 'border-emerald-400 ring-2 ring-emerald-200' : 'border-orange-300'} rounded-xl p-4 shadow-sm`} data-testid="bt-f2-card">
          <div className="flex items-center gap-2 mb-3">
            <span className="bg-orange-600 text-white text-xs font-black px-2 py-0.5 rounded">F2</span>
            <span className="text-sm font-bold text-orange-900">Next-Draw · Additive</span>
            {!f1Wins && <CheckCircle size={18} weight="fill" className="text-emerald-600 ml-auto" />}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <HitCell label="Top-1" pct={bt.f2?.top1_pct} hits={bt.f2?.top1_hits} n={bt.n} accent="orange" />
            <HitCell label="Top-3" pct={bt.f2?.top3_pct} hits={bt.f2?.top3_hits} n={bt.n} accent="orange" highlight />
            <HitCell label="Top-5" pct={bt.f2?.top5_pct} hits={bt.f2?.top5_hits} n={bt.n} accent="orange" />
          </div>
        </div>
      </div>

      {/* Verdict row */}
      <div className="bg-white border border-violet-200 rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="bt-verdict">
        <Trophy size={18} weight="fill" className="text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-gray-700">
          <strong className="text-violet-900">Verdict:</strong>{' '}
          {f1Wins ? (
            <>Formula 1 wins top-3 hit rate ({bt.f1?.top3_pct}% vs {bt.f2?.top3_pct}%) — long-term overdue signals are currently more accurate.</>
          ) : (
            <>Formula 2 wins top-3 hit rate ({bt.f2?.top3_pct}% vs {bt.f1?.top3_pct}%) — next-draw contextual signals are currently more accurate.</>
          )}
        </div>
      </div>

      {/* Auto-tune readout (only for sum-range which has it) */}
      {tune?.results && (
        <div className="bg-white border border-violet-200 rounded-lg p-3" data-testid="bt-tune">
          <div className="flex items-center gap-2 mb-2">
            <Lightning size={16} weight="fill" className="text-amber-500" />
            <span className="text-xs font-bold text-violet-900">Exponent Auto-Tune (F1)</span>
            {tune.best !== tune.current && (
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                <Warning size={11} weight="fill" /> Better exponent found: {tune.best}
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            {Object.entries(tune.results).map(([exp, r]) => (
              <div key={exp} className={`p-2 rounded border ${exp === tune.best ? 'bg-emerald-50 border-emerald-300' : exp === tune.current ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="font-mono font-bold">^{exp} {exp === tune.current && <span className="text-[9px] text-blue-700">(current)</span>} {exp === tune.best && exp !== tune.current && <span className="text-[9px] text-emerald-700">★ best</span>}</div>
                <div className="font-mono text-gray-700">Top-3: <span className="font-bold">{r.top3_pct}%</span> ({r.hits}/{r.n})</div>
              </div>
            ))}
          </div>
          {tune.best !== tune.current && (
            <p className="text-[10px] text-amber-700 mt-2 italic">
              💡 Switching the F1 exponent from {tune.current} to {tune.best} would improve top-3 accuracy on the last {bt.n} draws.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function HitCell({ label, pct, hits, n, accent = 'blue', highlight = false }) {
  const color = accent === 'orange' ? 'text-orange-700' : 'text-blue-700';
  const bg = highlight ? (accent === 'orange' ? 'bg-orange-50' : 'bg-blue-50') : 'bg-gray-50';
  return (
    <div className={`${bg} rounded-md p-2 text-center`} data-testid={`hitcell-${accent}-${label.toLowerCase()}`}>
      <div className="text-[9px] font-bold uppercase text-gray-500 tracking-wider">{label}</div>
      <div className={`font-mono font-black text-xl ${color}`}>{pct}%</div>
      <div className="text-[9px] text-gray-400 font-mono">{hits}/{n}</div>
    </div>
  );
}
